/**
 * 時刻の表示と入力。区間・ここ好きの時刻はミリ秒で持ち、画面では時分秒で見せる（仕様書「歌唱バージョンを登録する」）。
 */

/** 2:34 / 1:02:03 の形。負の値は 0 として扱う */
export function formatMs(ms: number, opts: { tenths?: boolean } = {}): string {
  const safe = Math.max(0, Math.floor(ms));
  const totalSec = Math.floor(safe / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const ss = String(s).padStart(2, "0");
  const tenth = opts.tenths ? `.${Math.floor((safe % 1000) / 100)}` : "";
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}${tenth}`;
  return `${m}:${ss}${tenth}`;
}

/** 読み上げ用：「2分34秒」 */
export function speakMs(ms: number): string {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h}時間`);
  if (m || h) parts.push(`${m}分`);
  parts.push(`${s}秒`);
  return parts.join("");
}

/**
 * 「1:02:03」「2:34」「2:34.5」「154」（秒）を受け付ける。読めなければ null。
 * 全角数字・全角コロンも受け付ける（スマホの日本語入力のまま打てるように）
 */
export function parseTime(input: string): number | null {
  const text = input
    .trim()
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[：]/g, ":")
    .replace(/[．。]/g, ".");
  if (!text) return null;
  if (!/^\d+(:\d{1,2}){0,2}(\.\d{1,3})?$/.test(text)) return null;
  const [main, frac = ""] = text.split(".");
  const parts = main.split(":").map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return null;
  if (parts.length > 1 && parts.slice(1).some((n) => n >= 60)) return null;
  let sec = 0;
  for (const p of parts) sec = sec * 60 + p;
  const ms = frac ? Number(frac.padEnd(3, "0").slice(0, 3)) : 0;
  return sec * 1000 + ms;
}

/** 元動画上の位置＝区間開始＋相対時刻（仕様書：10:00 開始＋2:34 ＝ 12:34） */
export function sourcePosition(segmentStartMs: number, relativeMs: number): number {
  return segmentStartMs + relativeMs;
}

/** 区間の長さ */
export function segmentLength(seg: { start_ms: number; end_ms: number }): number {
  return Math.max(0, seg.end_ms - seg.start_ms);
}

export type SegmentProblem = "start_negative" | "end_before_start" | "end_after_video" | "unknown_duration" | null;

/** 0 <= 開始 < 終了 <= 元動画長。動画長が分からないものは確定できない */
export function checkSegment(startMs: number, endMs: number, videoDurationMs: number | null | undefined): SegmentProblem {
  if (videoDurationMs == null || !Number.isFinite(videoDurationMs) || videoDurationMs <= 0) return "unknown_duration";
  if (startMs < 0) return "start_negative";
  if (endMs <= startMs) return "end_before_start";
  if (endMs > videoDurationMs) return "end_after_video";
  return null;
}

export const SEGMENT_PROBLEM_TEXT: Record<Exclude<SegmentProblem, null>, string> = {
  start_negative: "開始は 0:00 以降にしてください。",
  end_before_start: "終了は開始より後にしてください。",
  end_after_video: "終了が元動画の長さを超えています。",
  unknown_duration: "元動画の長さを確認できないため、登録を確定できません。",
};

const dateFmt = new Intl.DateTimeFormat("ja-JP", { year: "numeric", month: "numeric", day: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const clockFmt = new Intl.DateTimeFormat("ja-JP", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/** 日時は UTC で受け取り、見ている人の時間帯で出す（仕様書「データモデル」） */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : dateFmt.format(t);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : dateTimeFmt.format(t);
}

export function formatClock(epochMs: number): string {
  return clockFmt.format(epochMs);
}

/** 「3分前」など。1日以上は日付 */
export function formatAgo(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "たったいま";
  if (min < 60) return `${min}分前`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}時間前`;
  return dateFmt.format(t);
}

/* ---------------------------------------------------------------- 歌った日（日付だけの値） */

const pad2 = (n: number) => String(n).padStart(2, "0");

/** 見ている人の時間帯での今日（YYYY-MM-DD）。日付の入力欄の上限に使う */
export function todayInput(now: number = Date.now()): string {
  const d = new Date(now);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * 日付の入力（YYYY-MM-DD）を、API に送る日時（UTC）にする。
 * 「歌った日」は日付だけを表すので、その日の UTC 正午として送る（日本でも米国西海岸でも、同じ日付で表示される）。
 * 読めない日付は null
 */
export function dateInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = Date.UTC(y, mo - 1, d, 12);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return `${m[1]}-${m[2]}-${m[3]}T12:00:00Z`;
}

/** API の日時を、日付の入力欄の値（見ている人の時間帯の日付）にする */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : todayInput(t);
}

/** 歌った日の入力の問題（空は「指定なし」で問題なし） */
export function performedDateProblem(value: string, now: number = Date.now()): string | null {
  if (!value.trim()) return null;
  if (!dateInputToIso(value)) return "日付を読み取れませんでした。カレンダーから選んでください。";
  if (value.trim() > todayInput(now)) return "これから先の日付は入れられません。";
  return null;
}
